import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import {
  DxButtonModule,
  DxDataGridModule,
  DxFormModule,
  DxLoadIndicatorModule,
  DxResponsiveBoxModule,
  DxSelectBoxModule,
  DxTabPanelModule,
  DxToastModule,
} from "devextreme-angular";

import {
  OrderGridModule,
  CalculationsGridModule,
  UserProfileModule,
} from "./shared/components";
import { P3AuthGuardService } from "./shared/services";
import { HomeComponent } from "./pages/home/home.component";
import { ProfileComponent } from "./pages/profile/profile.component";
import { TasksComponent } from "./pages/tasks/tasks.component";
import { LoginSsoComponent } from "./pages/login-sso/login-sso.component";
import { NotFoundComponent } from "./pages/not-found/not-found.component";
import { UserManagementComponent } from "./pages/user-management/user-management.component";
import { ProduktManagementComponent } from "./pages/produkt-management/produkt-management.component";
import { ProduktComponent } from "./pages/produkt/produkt.component";
import { OptionManagementComponent } from "./pages/option-management/option-management.component";
import { PaperManagementComponent } from "./pages/paper-management/paper-management.component";
import { OrganizationManagementComponent } from "./pages/organization-management/organization-management.component";
import { CalculatorComponent } from "./pages/calculator/calculator.component";
import { CalculationsComponent } from "./pages/calculations/calculations.component";
import {
  ACCOUNT_MANAGER,
  ADMIN,
  ADMIN_US,
  EXTERNAL_USER_ROLES_DE,
  INTERNAL_USER_ROLES_DE,
  MANUFACTURER,
  SALES_MANAGER,
  USA_ROLES,
  KEY_USER_US,
  INTERNAL_USER_US,
  EXTERNAL_USER_US,
} from "./shared/model/roles";
import { PrintingTemplateComponent } from "./pages/printing-template/printing-template.component";
import { NotAuthorizedComponent } from "./pages/not-authorized/not-authorized.component";
import { WelcomeComponent } from "./pages/welcome/welcome.component";
import { ExternalFileUploadComponent } from "./pages/external-file-upload/external-file-upload.component";
import { PublisherConfigurationComponent } from "./pages/publisher-configuration/publisher-configuration.component";
import { HomeCardModule } from "./shared/components/home-card/home-card.component";
import { CommonModule } from "@angular/common";
import { SimpleOrderGridModule } from "./pages/home/simple-order-grid/simple-order-grid.component";
import { UploadGridWithOrdersModule } from "./pages/home/upload-grid-with-orders/upload-grid-with-orders.component";
import { NewsElementPopupModule } from "./shared/components/news-feed/news-element-popup/news-element-popup.component";
import { NewsElementKachelModule } from "./shared/components/news-feed/news-element-tile/news-element-tile.component";
import { ConfirmationDialogModule } from "./shared/components/confirmation-dialog/confirmation-dialog.component";
import { PapierManagementComponent } from "./pages/papier-management/papier-management.component";
import { CalculationGridModule } from "./pages/home/calculation-grid/calculation-grid.component";
import { CalculationsArchivedComponent } from "./pages/calculations-archived/calculations-archived.component";
import { CalculationsArchivedGridModule } from "./shared/components/calculations-archived-grid/calculations-archived-grid.component";
import { OptionMappingComponent } from "./pages/option-mapping/option-mapping.component";
import { MaintenanceGuardService } from "./shared/services/maintenance.guard.service";
import { MaintenanceComponent } from "./pages/maintenance/maintenance.component";
import { CalculatorRemasteredComponent } from "./pages/calculator-remastered/calculator-remastered.component";

const routes: Routes = [
  {
    path: "maintenance",
    component: MaintenanceComponent,
  },
  {
    path: "",
    component: HomeComponent,
    canActivate: [P3AuthGuardService, MaintenanceGuardService],
    data: {
      allowedRoles: [
        ...INTERNAL_USER_ROLES_DE,
        ...EXTERNAL_USER_ROLES_DE,
        ...USA_ROLES,
      ],
    },
  },
  {
    path: "profile",
    component: ProfileComponent,
    canActivate: [P3AuthGuardService, MaintenanceGuardService],
    data: {
      allowedRoles: [
        ...INTERNAL_USER_ROLES_DE,
        ...EXTERNAL_USER_ROLES_DE,
        ...USA_ROLES,
      ],
    },
  },
  {
    path: "login-sso",
    component: LoginSsoComponent,
    canActivate: [],
  },
  //
  // TODO: Has to be implemented with US GoLive
  //
  /*
  {
    path: "nutzungsbedingungen",
    component: TermsOfUseComponent,
  },
  {
    path: "agb",
    component: AgbComponent,
  },
  {
    path: "impressum",
    component: ImprintComponent,
  },
  {
    path: "datenschutz",
    component: PrivacyComponent,
  },
  */
  {
    path: "product",
    component: ProduktManagementComponent,
    canActivate: [P3AuthGuardService, MaintenanceGuardService],
    data: {
      allowedRoles: [ADMIN_US, KEY_USER_US],
    },
  },
  {
    path: "productDetails/:id",
    component: ProduktComponent,
    canActivate: [P3AuthGuardService, MaintenanceGuardService],
    data: {
      allowedRoles: [ADMIN, SALES_MANAGER, ADMIN_US, KEY_USER_US],
    },
  },
  {
    path: "option",
    component: OptionManagementComponent,
    canActivate: [P3AuthGuardService, MaintenanceGuardService],
    data: {
      allowedRoles: [ADMIN, SALES_MANAGER, ADMIN_US, KEY_USER_US],
    },
  },
  {
    path: "option-mapping",
    component: OptionMappingComponent,
    canActivate: [P3AuthGuardService, MaintenanceGuardService],
    data: {
      allowedRoles: [ADMIN_US, KEY_USER_US],
    },
  },
  {
    path: "paper-management",
    component: PaperManagementComponent,
    canActivate: [P3AuthGuardService, MaintenanceGuardService],
    data: {
      allowedRoles: [ADMIN_US, KEY_USER_US],
    },
  },
  {
    path: "components",
    component: PublisherConfigurationComponent,
    canActivate: [P3AuthGuardService, MaintenanceGuardService],
    data: {
      allowedRoles: [ADMIN_US],
    },
  },
  {
    path: "user",
    component: UserManagementComponent,
    canActivate: [P3AuthGuardService, MaintenanceGuardService],
    data: {
      allowedRoles: [ADMIN, SALES_MANAGER, ADMIN_US],
    },
  },
  {
    path: "organizations",
    component: OrganizationManagementComponent,
    canActivate: [P3AuthGuardService, MaintenanceGuardService],
    data: {
      allowedRoles: [ADMIN, SALES_MANAGER, ADMIN_US],
    },
  },
  {
    path: "calculator-legacy",
    component: CalculatorComponent,
    canActivate: [P3AuthGuardService, MaintenanceGuardService],
    data: {
      allowedRoles: [ADMIN_US],
    },
  },
  {
    path: "calculator",
    component: CalculatorRemasteredComponent,
    canActivate: [P3AuthGuardService, MaintenanceGuardService],
    data: {
      allowedRoles: [...USA_ROLES],
    },
  },
  {
    path: "calculator-legacy/:id",
    component: CalculatorComponent,
    canActivate: [P3AuthGuardService, MaintenanceGuardService],
    data: {
      allowedRoles: [ADMIN_US],
    },
  },
  {
    path: "notauthorized",
    component: NotAuthorizedComponent,
    canActivate: [MaintenanceGuardService],
  },
  {
    path: "welcome",
    component: WelcomeComponent,
    canActivate: [P3AuthGuardService, MaintenanceGuardService],
  },
  {
    path: "**",
    component: NotFoundComponent,
    canActivate: [MaintenanceGuardService],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, { useHash: true }),
    OrderGridModule,
    CalculationsGridModule,
    UserProfileModule,
    DxFormModule,
    DxResponsiveBoxModule,
    DxTabPanelModule,
    DxDataGridModule,
    HomeCardModule,
    DxSelectBoxModule,
    CommonModule,
    DxButtonModule,
    DxToastModule,
    DxLoadIndicatorModule,
    SimpleOrderGridModule,
    UploadGridWithOrdersModule,
    NewsElementPopupModule,
    NewsElementKachelModule,
    ConfirmationDialogModule,
    CalculationGridModule,
    CalculationsArchivedGridModule,
  ],
  providers: [P3AuthGuardService],
  exports: [RouterModule],
  declarations: [
    HomeComponent,
    ProfileComponent,
    TasksComponent,
    CalculationsComponent,
    CalculationsArchivedComponent,
  ],
})
export class AppRoutingModule {}
